import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getJobs, createJobManual, deleteJob, getClients, getAllUsersDropdown } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { Plus, Loader2, Search, X, Trash2, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import { SearchableSelect } from '../components/SearchableSelect';
import api from '../services/api';

const statusTabs = [
  { label: 'All', value: 99 },
  { label: 'To Book', value: 0 },
  { label: 'Booked', value: 1 },
  { label: 'On going', value: 2 },
  { label: 'Completed', value: 3 },
  { label: 'Canceled', value: -1 },
];

const statusColors = {
  '-1': 'bg-red-100 text-red-700',
  '0': 'bg-slate-200 text-slate-700',
  '1': 'bg-blue-100 text-blue-700',
  '2': 'bg-yellow-100 text-yellow-700',
  '3': 'bg-green-100 text-green-700',
};

const statusLabels = {
  '-1': 'Canceled',
  '0': 'To Book',
  '1': 'Booked',
  '2': 'On going',
  '3': 'Completed',
};

// Create Job Manually Modal
function CreateJobModal({ isOpen, onClose, onSubmit, loading, initialClientId }) {
  const [formData, setFormData] = useState({
    client_id: '', property_address_id: '', is_to_book: false,
    date_start: '', date_end: '', job_title: '', description: '',
    assignee_id: '', start_time: '', end_time: ''
  });
  const [addresses, setAddresses] = useState([]);

  // When the modal opens, seed the client_id from the URL (?client_id=...) if provided
  useEffect(() => {
    if (isOpen && initialClientId) {
      setFormData(prev => ({ ...prev, client_id: initialClientId, property_address_id: '' }));
    }
  }, [isOpen, initialClientId]);

  const { data: clients } = useQuery({
    queryKey: ['clientsDropdown'],
    queryFn: () => api.get('/api/dropdowns/clients').then(r => r.data),
    enabled: isOpen
  });
  const { data: users } = useQuery({
    queryKey: ['usersDropdown'],
    queryFn: () => getAllUsersDropdown().then(r => r.data),
    enabled: isOpen
  });

  useEffect(() => {
    if (formData.client_id) {
      api.get(`/api/clients/${formData.client_id}`).then(r => {
        const addrs = r.data.property_addresses || [];
        setAddresses(addrs);
        // If there's exactly one address, auto-select it for convenience.
        if (addrs.length === 1) {
          setFormData(prev => prev.property_address_id ? prev : { ...prev, property_address_id: addrs[0].id });
        }
      }).catch(() => setAddresses([]));
    } else {
      setAddresses([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters & api import are stable, only re-fetch when client changes
  }, [formData.client_id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.client_id) { toast.error('Please select a client'); return; }
    if (!formData.property_address_id) { toast.error('Please select a property address'); return; }
    if (!formData.job_title) { toast.error('Please enter a job title'); return; }
    onSubmit({
      client_id: formData.client_id,
      property_address_id: formData.property_address_id,
      is_to_book: formData.is_to_book,
      date_start: formData.date_start || null,
      date_end: formData.date_end || null,
      job_title: formData.job_title,
      description: formData.description,
      assignees: formData.assignee_id ? [{
        assignee_id: formData.assignee_id,
        start_time: formData.start_time,
        end_time: formData.end_time
      }] : []
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-job-manual-modal">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-lg">Create New Job</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Client <span className="text-red-500">*</span></label>
            <SearchableSelect
              value={formData.client_id}
              onChange={(v) => setFormData({ ...formData, client_id: v, property_address_id: '' })}
              options={(clients || []).map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Select Client"
              testId="manual-job-client"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Property Address <span className="text-red-500">*</span></label>
            <SearchableSelect
              value={formData.property_address_id}
              onChange={(v) => setFormData({ ...formData, property_address_id: v })}
              options={(addresses || []).map((a) => ({
                value: a.id,
                label: [a.address_line1, a.city, a.region, a.postal_code].filter(Boolean).join(', '),
              }))}
              placeholder="Select Property Address"
              disabled={!formData.client_id}
              testId="manual-job-address"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={formData.is_to_book} onChange={(e) => setFormData({...formData, is_to_book: e.target.checked})} className="rounded" />
              To book (no schedule)
            </label>
          </div>
          {!formData.is_to_book && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <DatePicker value={formData.date_start} onChange={(v) => setFormData({...formData, date_start: v})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <DatePicker value={formData.date_end} onChange={(v) => setFormData({...formData, date_end: v})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assignee</label>
                <select value={formData.assignee_id} onChange={(e) => setFormData({...formData, assignee_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                  <option value="">Select Assignee</option>
                  {users?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              {formData.assignee_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time</label>
                    <TimePicker value={formData.start_time} onChange={(v) => setFormData({...formData, start_time: v})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time</label>
                    <TimePicker value={formData.end_time} onChange={(v) => setFormData({...formData, end_time: v})} />
                  </div>
                </div>
              )}
            </>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Job Title <span className="text-red-500">*</span></label>
            <input type="text" value={formData.job_title} onChange={(e) => setFormData({...formData, job_title: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="manual-job-title" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={3} data-testid="manual-job-description" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2" data-testid="manual-job-submit">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Jobs() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialClientIdFromUrl = searchParams.get('client_id') || '';
  const [activeTab, setActiveTab] = useState(99);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [initialClientId, setInitialClientId] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [createdByFilter, setCreatedByFilter] = useState('');

  // If the URL contains ?client_id=..., open the Create Job modal pre-populated with that client.
  useEffect(() => {
    if (initialClientIdFromUrl) {
      setInitialClientId(initialClientIdFromUrl);
      setShowCreateJob(true);
      // Strip the query param so a page refresh/navigate-back doesn't re-open the modal.
      searchParams.delete('client_id');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: staffUsers } = useQuery({
    queryKey: ['usersDropdownAll'],
    queryFn: () => getAllUsersDropdown().then(r => r.data),
  });

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['jobs', activeTab, createdByFilter],
    queryFn: () => getJobs({ status: activeTab, user_filter: createdByFilter || undefined }).then(r => r.data),
  });

  const createJobMutation = useMutation({
    mutationFn: createJobManual,
    onSuccess: (res) => {
      toast.success(`Job ${res.data.job_number} created`);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setShowCreateJob(false);
    },
    onError: () => toast.error('Failed to create job')
  });

  const removeJobMutation = useMutation({
    mutationFn: (id) => deleteJob(id),
    onSuccess: () => {
      toast.success('Job has been removed');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setRemoveConfirm(null);
    },
    onError: () => toast.error('Failed to remove job')
  });

  const pageSize = 20;
  const filtered = jobs.filter(j => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      j.job_number?.toLowerCase().includes(s) ||
      j.quote_number?.toLowerCase().includes(s) ||
      j.client_name?.toLowerCase().includes(s) ||
      j.address?.toLowerCase().includes(s) ||
      j.job_titles?.toLowerCase().includes(s)
    );
  });
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">Job Sheets</h1>
        <button onClick={() => setShowCreateJob(true)} className="px-3 py-2 text-sm bg-primary text-white rounded hover:bg-primary-hover flex items-center gap-2" data-testid="create-job-btn">
          <Plus size={16} />
          Create Job
        </button>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-1" data-testid="job-status-tabs">
        {statusTabs.map(tab => (
          <button
            key={tab.value}
            onClick={() => { setActiveTab(tab.value); setPage(1); }}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              activeTab === tab.value
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid={`tab-${tab.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search jobs..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded text-sm focus:ring-1 focus:ring-primary"
            data-testid="job-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <User size={14} className="text-slate-400" />
          <select
            value={createdByFilter}
            onChange={(e) => { setCreatedByFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded text-sm min-w-[160px]"
            data-testid="jobs-created-by-filter"
          >
            <option value="">All Users</option>
            {staffUsers?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="jobs-table">
          <thead>
            <tr className="border-b bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Job No.</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Quote No.</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Client</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Address</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Jobs</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Completed</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Created By</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={10} className="py-8 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400">No Results Found</td></tr>
            ) : (
              paged.map(job => (
                <tr key={job.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`job-row-${job.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{job.date_added ? formatDate(job.date_added) : '-'}</td>
                  <td className="px-4 py-3 font-medium">{job.job_number}</td>
                  <td className="px-4 py-3">{job.quote_number || '-'}</td>
                  <td className="px-4 py-3">{job.client_name}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate">{job.address ? <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}`} target="_blank" rel="noopener noreferrer" className="text-[#3498db] hover:underline" data-testid="address-map-link">{job.address}</a> : '-'}</td>
                  <td className="px-4 py-3 max-w-[150px] truncate">{job.job_titles}</td>
                  <td className="px-4 py-3 text-center">{job.jobs_completed || '0 / 0'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-testid={`job-created-by-${job.id}`}>{job.added_by_name || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 text-xs rounded ${statusColors[job.status] || ''}`}>
                      {statusLabels[job.status] || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <button
                      onClick={() => setRemoveConfirm(job)}
                      className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 mr-1"
                      data-testid={`remove-job-${job.id}`}
                    >
                      Remove
                    </button>
                    <Link
                      to={`/jobs/${job.id}`}
                      className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      data-testid={`details-job-${job.id}`}
                    >
                      Details
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded hover:bg-slate-100 disabled:opacity-30">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded hover:bg-slate-100 disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateJobModal
        isOpen={showCreateJob}
        onClose={() => { setShowCreateJob(false); setInitialClientId(''); }}
        onSubmit={(data) => createJobMutation.mutate(data)}
        loading={createJobMutation.isPending}
        initialClientId={initialClientId}
      />

      {/* Remove Confirmation */}
      {removeConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="remove-job-modal">
          <div className="bg-white rounded-lg w-full max-w-sm p-6">
            <h3 className="font-semibold mb-2">Remove Job</h3>
            <p className="text-sm text-slate-600 mb-4">
              Are you sure you want to remove <b>{removeConfirm.job_number}</b> from the Job's list?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRemoveConfirm(null)} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button
                onClick={() => removeJobMutation.mutate(removeConfirm.id)}
                disabled={removeJobMutation.isPending}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-2"
                data-testid="confirm-remove-job"
              >
                {removeJobMutation.isPending && <Loader2 size={16} className="animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Jobs;
